import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import useCategoriesStore from "../../../store/categoriesStore";
import useMenusStore from "../../../store/menusStore";
import useAdminAPI from "../../../hooks/useAdminAPI";

const Categories = () => {
    const { categories, activeCategory, setActiveCategory } =
        useCategoriesStore();

    const { activeMenu } = useMenusStore();

    useEffect(() => {
        if (activeMenu) {
            setActiveCategory(
                categories.find((c) => c.menu === activeMenu._id)?._id
            );
        }
    }, [activeMenu]);

    const { addCategoryAPI, deleteCategoryAPI, updateCategoryAPI } =
        useAdminAPI();

    const [newCategoryTitle, setNewCategoryTitle] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingCategoryTitle, setPendingCategoryTitle] = useState("");

    const handleAddCategory = () => {
        setPendingCategoryTitle("");
        setShowCreateModal(true);
    };

    const confirmAddCategory = async () => {
        if (pendingCategoryTitle.trim() === "") return;
        try {
            const created = await addCategoryAPI({
                title: pendingCategoryTitle,
            });
            setPendingCategoryTitle("");
            if (created && created._id) {
                setActiveCategory(created._id);
            }
        } catch (error) {
            console.error("Fehler beim Hinzufügen der Kategorie:", error);
        } finally {
            setShowCreateModal(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCategory = async () => {
        try {
            await deleteCategoryAPI(activeCategory);
        } catch (error) {
            console.error("Fehler beim Löschen der Kategorie:", error);
        } finally {
            setShowDeleteModal(false);
        }
    };

    const handleSortOrderChange = async (e) => {
        const updatedSortOrder = parseInt(e.target.value, 10);

        const activeCategoryObj = categories.find(
            (category) => category._id === activeCategory
        );

        const updatedCategory = {
            ...activeCategoryObj,
            sortOrder: updatedSortOrder,
        };

        try {
            await updateCategoryAPI(updatedCategory);
        } catch (error) {
            console.error("Error updating sort order:", error);
        }
    };

    const getSortOrderOfCategory = (categoryId) => {
        for (let i = 0; i < categories.length; i++) {
            if (categories[i]._id === categoryId)
                return categories[i].sortOrder;
        }
        return 0;
    };

    return (
        <div className="p-4 bg-gray-100">
            <div className="flex items-center justify-end mb-4 gap-2">
                <button
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded flex items-center gap-2 shadow-sm transition"
                    onClick={handleAddCategory}
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    Kategorie hinzufügen
                </button>
                {activeCategory && (
                    <button
                        className="bg-red-500 hover:bg-red-600 text-white font-medium px-4 py-2 rounded flex items-center gap-2 shadow-sm transition"
                        onClick={handleDeleteCategory}
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                        Kategorie löschen
                    </button>
                )}
            </div>
            <h1 className="text-2xl font-bold mb-4">Kategorien</h1>

            <select
                className="w-full p-2 border border-gray-300 rounded-md mb-4"
                onChange={(e) => setActiveCategory(e.target.value)}
                value={activeCategory || ""}
            >
                {categories
                    .filter((category) => category.menu === activeMenu._id)
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    .map((category) => (
                        <option key={category._id} value={category._id}>
                            {`(${category.sortOrder ?? 0}) ${category.title}`}
                        </option>
                    ))}
            </select>

            {/* Sort Order Input */}
            {activeCategory && (
                <div className="mb-4">
                    <label className="block font-medium mb-2">
                        Sortierreihenfolge
                    </label>
                    <input
                        type="number"
                        value={getSortOrderOfCategory(activeCategory) || 0}
                        onChange={handleSortOrderChange}
                        className="border rounded-md p-2 w-full"
                    />
                </div>
            )}

            {/* Add Category */}
            <Dialog
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                className="fixed z-50 inset-0 flex items-center justify-center"
            >
                <div
                    className="fixed inset-0 bg-black opacity-30"
                    aria-hidden="true"
                />
                <div className="relative bg-white rounded-lg shadow-lg p-6 mx-auto max-w-sm">
                    <Dialog.Title className="text-lg font-bold mb-2">
                        Neue Kategorie erstellen
                    </Dialog.Title>
                    <Dialog.Description className="mb-4">
                        Bitte gib den Namen der neuen Kategorie ein:
                    </Dialog.Description>
                    <input
                        type="text"
                        placeholder="Kategoriename"
                        value={pendingCategoryTitle}
                        onChange={(e) =>
                            setPendingCategoryTitle(e.target.value)
                        }
                        className="border rounded-md p-2 w-full mb-4"
                        autoFocus
                    />
                    <div className="flex justify-end space-x-2">
                        <button
                            onClick={() => setShowCreateModal(false)}
                            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={confirmAddCategory}
                            className="px-4 py-2 rounded bg-green-500 text-white hover:bg-green-700"
                            disabled={pendingCategoryTitle.trim() === ""}
                        >
                            Erstellen
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* Delete Category */}
            {activeCategory && (
                <Dialog
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    className="fixed z-50 inset-0 flex items-center justify-center"
                >
                    <div
                        className="fixed inset-0 bg-black opacity-30"
                        aria-hidden="true"
                    />
                    <div className="relative bg-white rounded-lg shadow-lg p-6 mx-auto max-w-sm">
                        <Dialog.Title className="text-lg font-bold mb-2">
                            Kategorie löschen bestätigen
                        </Dialog.Title>
                        <Dialog.Description className="mb-4">
                            Möchten Sie die Kategorie wirklich löschen?
                            <br />
                            <span className="font-semibold text-red-600">
                                {
                                    categories.find(
                                        (c) => c._id === activeCategory
                                    )?.title
                                }
                            </span>
                        </Dialog.Description>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={confirmDeleteCategory}
                                className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-700"
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default Categories;
